(function () {
  'use strict';

  if (window.DieuxScrollSpy) return;

  const REQUEST_TIMEOUT_MS = 1200;
  const RESIZE_DEBOUNCE_MS = 150;

  /**
   * Tracks which of a set of in-page targets is currently being read.
   *
   * @param {object} options - Tracking setup.
   * @param {Element[]} options.targets - Tracked elements, in document order.
   * @param {(target: Element) => string} options.idOf - Anchor id of a target.
   * @param {(id: string, changed: boolean) => void} options.onCurrent - Called with the target now being read. `changed` is false when the same target is re-committed, which a repeated jump to the current anchor still does.
   * @param {{host: Element, property: string}} [options.tail] - Where to publish the reserved trailing room. Omitted, nothing is reserved.
   * @param {() => void} [options.onSync] - Runs last in the same read phase as the region and tail measurements, for an owner that resolves something of its own from layout.
   * @param {string} [options.name] - Label for the coalescing read key.
   * @returns {object|null} Tracker, or `null` when there is nothing to track.
   */
  function create(options) {
    const opts = options || {};
    const targets = Array.isArray(opts.targets)
      ? opts.targets.filter((element) => element instanceof Element)
      : [];
    const idOf = typeof opts.idOf === 'function' ? opts.idOf : null;
    const onCurrent = typeof opts.onCurrent === 'function' ? opts.onCurrent : null;
    const onSync = typeof opts.onSync === 'function' ? opts.onSync : null;
    const label = typeof opts.name === 'string' ? opts.name : 'scroll-spy';
    const tail =
      opts.tail && opts.tail.host instanceof Element && typeof opts.tail.property === 'string'
        ? opts.tail
        : null;

    if (targets.length < 2 || !idOf || !onCurrent) return null;

    const ids = targets.map((target) => idOf(target));
    const readKey = Symbol(`${label}-read`);

    let currentId = null;
    let requestedId = null;
    let requestTimer = 0;
    let resizeTimer = 0;
    let rootMargin = '';
    let observer = null;
    let inBand = new Set();
    let aboveRegion = new Set();
    let started = false;
    let destroyed = false;

    function has(id) {
      return ids.includes(id);
    }

    /**
     * Where the top of the tracking region is, as CSS declares it.
     *
     * @returns {number} Offset in pixels from the top of the viewport.
     */
    function bandLine() {
      return Number.parseFloat(getComputedStyle(targets[0]).scrollMarginBlockStart) || 0;
    }

    /**
     * @returns {string|null} The anchor in the URL, when it is one of ours.
     */
    function idFromHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return null;

      let decoded = hash;
      try {
        decoded = decodeURIComponent(hash);
      } catch {
        // A malformed hash is simply not one of our anchors.
      }
      return has(decoded) ? decoded : null;
    }

    function commit(id) {
      if (!has(id)) return;

      const changed = currentId !== id;
      currentId = id;
      onCurrent(id, changed);
    }

    /**
     * Commits a target the customer explicitly asked for and holds tracking on it
     * until it arrives, so an in-flight jump does not drag the indicator through
     * every target it passes.
     *
     * @param {string} id - Anchor being navigated to.
     * @returns {void}
     */
    function request(id) {
      if (destroyed || !has(id)) return;

      requestedId = id;
      commit(id);
      clearTimeout(requestTimer);
      requestTimer = window.setTimeout(() => {
        requestTimer = 0;
        requestedId = null;
      }, REQUEST_TIMEOUT_MS);
    }

    function handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          inBand.add(entry.target);
          aboveRegion.delete(entry.target);
          return;
        }

        inBand.delete(entry.target);
        const above = entry.rootBounds
          ? entry.boundingClientRect.bottom <= entry.rootBounds.top
          : false;
        if (above) aboveRegion.add(entry.target);
        else aboveRegion.delete(entry.target);
      });

      let next = targets.find((target) => inBand.has(target));

      if (!next && aboveRegion.size > 0) {
        next = targets.at(-1);
      }
      if (!next) return;

      const id = idOf(next);

      if (requestedId) {
        if (id !== requestedId) return;
        requestedId = null;
        clearTimeout(requestTimer);
        requestTimer = 0;
      }

      commit(id);
    }

    /** Rebuilds the tracking region, but only when it actually moved. */
    function syncObserver() {
      const nextMargin = `-${bandLine()}px 0px 0px 0px`;
      if (nextMargin === rootMargin && observer) return;

      rootMargin = nextMargin;
      observer?.disconnect();
      inBand = new Set();
      aboveRegion = new Set();
      observer = new IntersectionObserver(handleIntersect, { rootMargin: nextMargin });
      targets.forEach((target) => observer.observe(target));
    }

    /**
     * Reserves the trailing scroll the closing targets need to reach the band
     * line, through the shared measurement in `assets/frame.js`.
     */
    function syncTail() {
      if (!tail) return;

      window.DieuxFrame?.reserveTailRoom({
        target: targets.at(-1),
        line: bandLine(),
        host: tail.host,
        property: tail.property,
      });
    }

    function read() {
      if (destroyed) return;

      syncObserver();
      syncTail();
      onSync?.();
    }

    /**
     * Re-resolves the region, the reserved room, and the owner's own sync.
     *
     * @returns {void}
     */
    function sync() {
      if (destroyed) return;
      if (window.DieuxFrame) {
        window.DieuxFrame.measure(readKey, read);
        return;
      }
      read();
    }

    function onHashChange() {
      const id = idFromHash();
      if (id) request(id);
    }

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(sync, RESIZE_DEBOUNCE_MS);
    }

    /**
     * @param {string} [initialId] - Target to commit before the first measurement.
     * @returns {void}
     */
    function start(initialId) {
      if (destroyed || started) return;
      started = true;

      if (initialId && has(initialId)) commit(initialId);

      window.addEventListener('hashchange', onHashChange);
      window.addEventListener('resize', onResize, { passive: true });

      sync();
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;

      observer?.disconnect();
      observer = null;
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('resize', onResize);
      window.DieuxFrame?.cancelMeasure(readKey);
      clearTimeout(requestTimer);
      clearTimeout(resizeTimer);
      requestTimer = 0;
      resizeTimer = 0;
      inBand = new Set();
      aboveRegion = new Set();
    }

    return {
      start,
      request,
      sync,
      destroy,
      has,
      idFromHash,
      bandLine,
      get currentId() {
        return currentId;
      },
    };
  }

  window.DieuxScrollSpy = Object.freeze({
    create,
    REQUEST_TIMEOUT_MS,
    RESIZE_DEBOUNCE_MS,
  });
})();
